/**
 * Deduplicator — prevents duplicate in-flight requests for the same key.
 *
 * This is separate from CacheManager because deduplication is about
 * coordinating concurrent FETCHES, not about storing cached data.
 * The caller wraps their fetcher with `dedupe()` to ensure only one
 * HTTP call fires for N concurrent callers requesting the same key.
 *
 * Usage:
 *   const dedup = new Deduplicator();
 *
 *   // Multiple concurrent calls with the same key → only 1 HTTP request
 *   const data = await dedup.dedupe('GET_COUNTRIES', () =>
 *     ActionRegistry.executeData('GET_COUNTRIES', {})
 *   );
 */

export class Deduplicator {
  private inflight = new Map<string, Promise<unknown>>();

  /**
   * If a request with the same key is already in-flight, piggyback on it.
   * Otherwise, execute the fetcher and share its result with any concurrent callers.
   */
  dedupe<T = unknown>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const existing = this.inflight.get(key);
    if (existing) return existing as Promise<T>;

    const promise = fetcher()
      .then((data) => {
        this.inflight.delete(key);
        return data;
      })
      .catch((err) => {
        this.inflight.delete(key);
        throw err;
      });

    this.inflight.set(key, promise);
    return promise;
  }

  /** Check if a request is currently in-flight for the given key. */
  hasPending(key: string): boolean {
    return this.inflight.has(key);
  }

  /** Number of currently in-flight requests. */
  get pendingCount(): number {
    return this.inflight.size;
  }

  /** Cancel tracking of all pending requests (does NOT abort the actual HTTP calls). */
  clear(): void {
    this.inflight.clear();
  }
}

/**
 * Global deduplicator singleton — shared across all engines.
 * Ensures app-wide dedup for the same action + params key.
 */
export const globalDedup = new Deduplicator();
